import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { LineOfBusiness } from 'api/src/services/Constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import update from 'lodash/update';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { CountyStateSelectInput } from '../../../common/CountyStateSelectInput';
import { LocationDropdown } from '../../../common/PSXHeader/SearchBar/LocationDropdown';
import {
  CloseButtonContainer,
  Input,
} from '../../../common/PSXHeader/SearchBar/LocationInput/LocationInput.styled';
import {
  IconContainer,
  LocationSearchPiece,
} from '../../../common/PSXHeader/SearchBar/SearchBar.styled';
import { setGeoLocationToStorage } from '../../../common/PSXHeader/SearchBar/utils';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useGeoLocationStorage } from '../../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../../hooks/useLagoon';
import { useLocation } from '../../../hooks/useLocation';
import { useGuestAcquisitionStore } from '../../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import { refactorPlaceName } from '../../../utils/locationSearch.util';
import { getLocationRequirement } from '../Guest.utils';
import { getBlueGreenGQLApiUrl, isLawwGuest } from '../utils';
import {
  LocationInputContainer,
  LocationSearchSpacer,
  LocationSearchWrapper,
} from './GuestLocation.style';
import { GuestLocation } from './GuestLocation.utils';
import { PostalLocationData } from './GuestLocationInput';
import {
  LocationInputCloseButtonStyle,
  LocationInputRow,
  LocationSearchLabel,
  LocationSearchPieceContainer,
  LocationInputContainer as SearchBarLocationInputContainer,
} from './GuestLocationSearch.style';

export type GuestLocationSearchProps = {
  isLocationModal?: boolean;
  isDataLoaded: boolean;
  isCountyDropdownLoading?: boolean;
  lob?: string;
  location?: GuestLocation;
  setLocation: (a: GuestLocation) => void;
  currentInputValue?: string;
  setCurrentInputValue?: (value: string) => void;
  selectedCounty?: string;
  setSelectedCounty: (value: string) => void;
  showCountyDropdown?: boolean;
  setShowCountyDropdown: (value: boolean) => void;
  usPostalPointId?: string;
  onSubmit?: (selectedLoc?: GuestLocation) => void;
  coverageType?: string;
  onPostalLocationSelected?: (data: PostalLocationData) => void;
};

export const GuestLocationSearch = ({
  isLocationModal,
  isDataLoaded,
  isCountyDropdownLoading = false,
  lob = '',
  coverageType = '',
  location,
  setLocation,
  setCurrentInputValue = () => {},
  selectedCounty = '',
  setSelectedCounty,
  showCountyDropdown = false,
  usPostalPointId = '',
  onSubmit,
  onPostalLocationSelected,
}: GuestLocationSearchProps) => {
  const { t } = useTranslation();
  const locationSearchInputId = useId();
  const locationAlertId = 'location-instructions';
  const wrapperRef = useRef<HTMLDivElement>(null);
  const displaySelectButton = !isLocationModal;
  const [isOnFocus, setIsOnFocus] = useState(false);
  const geoLocation = useGeoLocationStorage();

  const [searchParams] = useSearchParams();
  const flags = useFlags();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const isLocationModalInFocus = isOnFocus && isLocationModal;
  const isLawwGuestExperience = isLawwGuest();
  const isButtonDisabled =
    !location?.label ||
    !isDataLoaded ||
    (showCountyDropdown && selectedCounty === '');

  const isButtonDisabledForLaww =
    !location?.label || (showCountyDropdown && selectedCounty === '');
  const isGEHAV2 = shouldUseGehaV2Styling();
  const lagoonConfigTable = useLagoon('config');
  const config =
    typeof lagoonConfigTable === 'function'
      ? lagoonConfigTable()
      : lagoonConfigTable;

  const [enableXIconFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LOCATION_SEARCH_TEXTBOX_ICON,
  ]);
  const enableXIcon = enableXIconFlag ?? false;

  const [cursor, setCursor] = useState(-1);
  const [isCurrentLocation, setIsCurrentLocation] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [locationInputVal, setLocationInputVal] = useState(
    location?.label || ''
  );
  const [locationSuggestions, setLocationSuggestions] = useState<object[]>([]);
  const isShowingLocationDropdown = isOnFocus;

  useEffect(() => {
    if (geoLocation?.name && geoLocation.name !== locationInputVal) {
      setLocationInputVal(geoLocation.name);
      setCurrentInputValue(geoLocation.name);
      setLocation({
        latitude: geoLocation.latitude,
        longitude: geoLocation.longitude,
        label: geoLocation.name,
        stateCode: geoLocation.stateCode,
      } as GuestLocation);
    }
  }, [geoLocation?.name, isOnFocus]);

  const { isNavigatingMaestro, requestData } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      isNavigatingMaestro: state.isNavigatingMaestro,
      requestData: state.requestData,
    }))
  );

  useEffect(() => {
    if (!isNavigatingMaestro) return;
    const zip = requestData?.zip;
    if (!zip?.trim().length) return;
    const countryCodes =
      config?.find(
        (item) => item.key === 'ADDITIONAL_COUNTRIES_FOR_LOCATION_SEARCH'
      )?.value ?? '';
    handleLocationChange(zip, countryCodes ?? '');
  }, [isNavigatingMaestro, requestData]);

  const [locationSearchResults, getLocation] = useLocation({
    url: guestBGApiUrl,
    onCompleted: (result: {
      data: {
        location: {
          features: [
            { place_name: string; id: string; center: [string, string] }
          ];
        };
      };
    }) => {
      setIsLocationLoading(false);
      const resultSuggestions = cloneDeep(result?.data?.location?.features);
      setIsCurrentLocation(!!resultSuggestions?.length);
      resultSuggestions?.map((loc) =>
        update(loc, 'place_name', () => refactorPlaceName(loc.place_name))
      );
      setLocationSuggestions(resultSuggestions);
      const location = resultSuggestions?.[0];
      if (isNavigatingMaestro && location) {
        processLocationSelection(location);
        setLocationInputVal(location?.place_name);
      }
    },
    onError: () => {
      setIsLocationLoading(false);
      setLocationSuggestions([]);
    },
  });

  const handleCloseLocationDropdown = () => {
    setIsOnFocus(false);
    setCursor(-1);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOutsideWrapper = wrapperRef.current
        ? !wrapperRef.current.contains(event.target as Node)
        : true;
      if (isOutsideWrapper) {
        handleCloseLocationDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const handleLocationChange = async (
    loc: string,
    additionalCountriesCode: string
  ) => {
    const locationRequirement = getLocationRequirement(lob, coverageType);
    const hasCountyRequirement = locationRequirement === 'county';
    await getLocation({
      variables: {
        address: encodeURIComponent(loc),
        countySearchEnabled: true,
        additionalCountriesCode,
        suppressRegions: hasCountyRequirement,
      },
    });
  };

  const handleLocationSearchTextChange = useCallback(
    debounce(async ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setCursor(-1);
      const countryCodes =
        config?.find(
          (item) => item.key === 'ADDITIONAL_COUNTRIES_FOR_LOCATION_SEARCH'
        )?.value ?? '';
      if (value?.trim().length) {
        setIsLocationLoading(true);
        await handleLocationChange(value, countryCodes ?? '');
      }
    }, 500),
    []
  );

  const handleSelection = (selectedLoc: GuestLocation) => {
    setLocation(selectedLoc);
    setLocationInputVal(selectedLoc.label);
    setCurrentInputValue(selectedLoc.label);
    handleCloseLocationDropdown();
    if (isNavigatingMaestro) {
      onSubmit?.(selectedLoc);
    }
  };

  const normalizedLob = lob.toUpperCase();
  const isGeospatialPostalLob = [
    LineOfBusiness.MR,
    LineOfBusiness.CNS,
  ].includes(normalizedLob as LineOfBusiness);

  const processLocationSelection = (targetLocation: any) => {
    const selectedLocation = {
      latitude: targetLocation.center[1],
      longitude: targetLocation.center[0],
      label: targetLocation.place_name,
      stateCode: targetLocation.stateCode,
      locationType: targetLocation.locationType,
    } as GuestLocation;
    const {
      id: locationId,
      center = [],
      place_name: placeName,
      stateCode: targetStateCode,
    } = targetLocation;
    const [selectedLong, selectedLat] = center;
    setGeoLocationToStorage({
      id: locationId,
      name: placeName,
      longitude: selectedLong,
      latitude: selectedLat,
      stateCode: targetStateCode,
    });

    const isPostcodeSelection =
      isGeospatialPostalLob &&
      targetLocation?.locationType?.toLowerCase() === 'postcode';
    const isPlacecodeSelection =
      normalizedLob === LineOfBusiness.MR &&
      targetLocation?.locationType?.toLowerCase() === 'place';
    const shouldUsePostalPointApi = isPostcodeSelection || isPlacecodeSelection;
    if (shouldUsePostalPointApi) {
      const variables = isPostcodeSelection
        ? { postal: String(targetLocation.zipCode) }
        : {
            city: String(targetLocation.place_name.split(',')[0])
              ?.toUpperCase()
              .trim(),
            state: String(targetLocation.stateCode)?.toUpperCase().trim(),
          };
      onPostalLocationSelected?.({
        selectedLocation,
        variables,
      });
      handleCloseLocationDropdown();
    }

    handleSelection({
      label: placeName,
      latitude: String(selectedLat),
      longitude: String(selectedLong),
      stateCode: targetStateCode,
    });
  };

  const handleLocationSearchKeyDown = (ev: {
    key: string;
    preventDefault: () => void;
  }) => {
    const minCursor = -1;
    const maxCursor =
      locationSearchResults?.data?.location?.features?.length ?? 0;

    if (
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].some(
        (key) => key === ev.key
      )
    ) {
      ev.preventDefault();
    }

    if (ev.key === 'Enter') {
      ev.preventDefault();
      if (cursor > minCursor && cursor < maxCursor) {
        const selectedLocation =
          locationSearchResults?.data?.location?.features?.[cursor];
        processLocationSelection(selectedLocation);
      }
    } else if (
      (ev.key === 'ArrowUp' || ev.key === 'ArrowLeft') &&
      cursor > minCursor
    ) {
      setCursor((previous) => Math.max(0, previous - 1));
    } else if (
      (ev.key === 'ArrowDown' || ev.key === 'ArrowRight') &&
      cursor < maxCursor
    ) {
      setCursor((previous) => Math.min(maxCursor - 1, previous + 1));
    }
  };

  const onLocationSearchInputFocus = () => {
    setTimeout(() => {
      setIsOnFocus(true);
      setCursor(-1);
    }, 100);
  };

  return (
    <React.Fragment>
      <LocationSearchWrapper
        isHighlighted={isLocationModal && isShowingLocationDropdown}
        isLocationModal={isLocationModal}
        ref={wrapperRef}
      >
        <LocationInputContainer
          id="location-input-container"
          isLocationModal={isLocationModal}
          isLocationModalInFocus={isLocationModalInFocus}
        >
          <div id="location-search-input-area">
            <SearchBarLocationInputContainer>
              <LocationInputRow>
                <LocationSearchPieceContainer data-testid="location-search-container">
                  <LocationSearchLabel
                    cssProps={{ isGEHAV2 }}
                    data-testid="location-search-label"
                    htmlFor={locationSearchInputId}
                  >
                    {t('Location')}
                  </LocationSearchLabel>
                  <LocationSearchPiece>
                    <IconContainer>
                      <IconSymbol
                        color="$icColor"
                        icon="location_on"
                        size={24}
                      />
                    </IconContainer>
                    <Input
                      aria-activedescendant={
                        isShowingLocationDropdown
                          ? 'location-search-result-suggestions'
                          : ''
                      }
                      aria-autocomplete="list"
                      aria-controls="location-search-result-suggestions"
                      aria-describedby={locationAlertId}
                      aria-expanded={isShowingLocationDropdown}
                      aria-haspopup="listbox"
                      autoComplete="off"
                      cssProps={{ isGEHAV2 }}
                      data-testid="location-search-input"
                      id={locationSearchInputId}
                      onChange={(e) => {
                        setLocationInputVal(e.target.value);
                        setCurrentInputValue(e.target.value);
                        handleLocationSearchTextChange(e);
                      }}
                      onFocus={onLocationSearchInputFocus}
                      onKeyDown={handleLocationSearchKeyDown}
                      role="combobox"
                      type="text"
                      value={locationInputVal}
                    />
                  </LocationSearchPiece>
                </LocationSearchPieceContainer>

                {enableXIcon && isShowingLocationDropdown ? (
                  <Layout.Group css={LocationInputCloseButtonStyle}>
                    <CloseButtonContainer
                      aria-label="Clear location"
                      data-testid="location-search-x-button"
                      onBlur={() => {
                        setTimeout(() => {
                          if (
                            !wrapperRef.current?.contains(
                              document.activeElement
                            )
                          ) {
                            setIsOnFocus(false);
                          }
                        }, 0);
                      }}
                      onClick={() => {
                        setLocationInputVal('');
                        setCurrentInputValue('');
                      }}
                      onFocus={onLocationSearchInputFocus}
                    >
                      X
                    </CloseButtonContainer>
                  </Layout.Group>
                ) : null}
              </LocationInputRow>
            </SearchBarLocationInputContainer>
          </div>
          {displaySelectButton && (
            <div style={{ alignSelf: 'center' }}>
              <Button
                data-auto-testid="submit-button"
                data-testid="submit-button"
                isDisabled={
                  isLawwGuestExperience
                    ? isButtonDisabledForLaww
                    : isButtonDisabled
                }
                onClick={() => onSubmit?.()}
                size="$md"
                type="submit"
              >
                {t('COMPARE_BUTTONS.SELECT')}
              </Button>
            </div>
          )}
        </LocationInputContainer>
        {isShowingLocationDropdown ? (
          <LocationDropdown
            activeSuggestion={cursor}
            cssProps={{ isGuestLocationSearch: true }}
            data-testid="location-search-bar-dropdown"
            id={locationAlertId}
            isCurrentLocation={isCurrentLocation}
            isLocationLoading={isLocationLoading}
            locationInputVal={locationInputVal}
            locationSuggestions={locationSuggestions}
            onClose={handleCloseLocationDropdown}
            selectedLocationLabel={location?.label}
            setIsCurrentLocation={setIsCurrentLocation}
            setLocationInputVal={setLocationInputVal}
            setLocationSuggestions={setLocationSuggestions}
            setSelectedLocation={processLocationSelection}
          />
        ) : null}
      </LocationSearchWrapper>
      <LocationSearchSpacer />
      {showCountyDropdown && (
        <div style={{ marginTop: '8px' }}>
          <Text data-testid="geospatial-modal-heading">
            {lob === LineOfBusiness.MR
              ? t('GUEST_COUNTY_STATE_MODAL.EDIT_SEARCH_COUNTY')
              : t('GUEST_COUNTY_STATE_MODAL.EDIT_SEARCH_STATE')}
          </Text>
          <div style={{ marginTop: '8px', marginBottom: '16px' }}>
            <CountyStateSelectInput
              data-testid="geospatial-county-dropdown"
              isCountyList={lob === LineOfBusiness.MR}
              onChange={(value: string | number | Function) =>
                setSelectedCounty(String(value))
              }
              placeholder={t('GUEST_COUNTY_STATE_MODAL.SELECT') as string}
              selectedValue={selectedCounty}
              show={!isCountyDropdownLoading}
              usPostalPointId={usPostalPointId}
              width={300}
            />
          </div>
        </div>
      )}
    </React.Fragment>
  );
};
